import { Console } from '@/shared/providers/custom-logger';
import { inject, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { AlertModel } from '@models/alert.model';
import { AppStore } from '@services/store/app.store';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Servizio per la gestione degli alert.
 *
 * Utilizzo:
 *
 * 1. Utilizzo diretto con async/await:
 *
 * async myFunction() {
 *   const alertOpt = new AlertModel();
 *   alertOpt.header = 'Titolo';
 *   alertOpt.message = 'Messaggio';
 *   alertOpt.buttons = ['OK'];
 *   await this.alertService.presentAlert(alertOpt);
 * }
 *
 * 2. Utilizzo in una pipe rxjs:
 *
 * from(this.alertService.presentAlert(alertOpt)).pipe(
 *   // ... resto della pipe
 * ).subscribe();
 *
 * Il metodo presentAlert è asincrono e restituisce una Promise<HTMLIonAlertElement>.
 * Deve essere utilizzato con await all'interno di una funzione async.
 *
 * Per chiudere un alert, utilizzare il metodo chiudiAlert() che restituisce un Observable<void>.
 * 
 * 
 * 3. Bloccante
 * 
 *   let alert: HTMLIonAlertElement | undefined;   //Serve per definire l'alert
 *   ...
 *   alert = await this.alertService.showAlert(AlertType.Errore, 'Errore di registrazione', error.message , error.error);
 *   await alert.onDidDismiss();
 *
 */

// Fuori dalla classe!
export enum AlertType {
  Errore = 'errore',
  Info = 'info'
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private alertController = inject(AlertController);
  private alert!: HTMLIonAlertElement;
  private alertOpt: AlertModel = new AlertModel();
  private appStore = inject(AppStore);

  /**
   * Mostra un alert personalizzato utilizzando le opzioni fornite.
   * @param alertOpt Oggetto AlertModel con le opzioni dell'alert
   * @returns Promise con l'istanza dell'alert creato
   */
  async presentAlert(alertOpt: AlertModel): Promise<HTMLIonAlertElement> {
    this.alert = await this.alertController.create(alertOpt);
    await this.alert.present();
    return this.alert;
  }

  /**
   * Mostra un alert con tipo, header, subHeader e messaggio di errore.
   * @param alertType Tipo di alert (Errore o Info)
   * @param header Titolo dell'alert
   * @param subHeader Sottotitolo dell'alert
   * @param errorMessage Messaggio da mostrare nell'alert
   * @returns Promise con l'istanza dell'alert creato
   */
  async showAlert(alertType: AlertType, header: string, subHeader: string, errorMessage: string): Promise<HTMLIonAlertElement> {
    const options: any = {
      header,
      subHeader,
      message: errorMessage,
      backdropDismiss: false,   // Devi per forza premere il pulsante OK per chiudere l'alert
      buttons: [
        {
          text: 'OK',
          handler: () => {
            Console.log('Pulsante OK cliccato', '', 'std');
          }
        }
      ]
    };

    const user = this.appStore.user();
    let username = '';
    if (user && user.profili && user.profili.length > 0) {
      // Recupera il ruolo attivo
      const activeRole = this.appStore.activeRole ? this.appStore.activeRole() : null;
      if (activeRole) {
        // Cerca il profilo predefinito/attivo
        const profiloAttivo = user.profili.find(p => p.ruolo === activeRole);
        //Se il profilo attivo ha un username, lo aggiungo all'alert con un trattino
        if (profiloAttivo) {
          username = profiloAttivo.username && profiloAttivo.username !== '' ? ' - ' + profiloAttivo.username : '';
        }
      }
      options.message = '<br>' + errorMessage + '<br><br><i> ' + user?.email + username + '</i>';
    }
    else {
      //Se non c'è un profilo attivo, mostro solo il messaggio di errore
      options.message = '<br>' + errorMessage + '<br><br>';

    }

    switch (alertType) {
      case AlertType.Errore:
        options.cssClass = 'alert-error';
        break;
      case AlertType.Info:
        options.cssClass = 'alert-info';
        break;
      default:
        options.cssClass = 'alert-info';
        break;
    }
    const alert = await this.alertController.create(options);
    await alert.present();
    return alert;
  }

  /**
   * Chiude l'alert attualmente aperto.
   * @returns Observable che completa quando l'alert è stato chiuso
   */
  chiudiAlert(): Observable<void> {
    return from(this.alertController.dismiss()).pipe(
      map(() => void 0)
    );
  }
}

export { AlertController };
